<template>
  <b-card title="Полоски">
    <div class="mb-1 d-flex flex-wrap" style="gap: 8px">
      <b-button variant="primary" :to="{ name: 'strip-builder-create' }">
        <feather-icon icon="PlusCircleIcon" class="mr-50" />
        Создать полоску
      </b-button>
      <b-button variant="outline-primary" :to="{ name: 'strip-import-archive' }">
        <feather-icon icon="UploadIcon" class="mr-50" />
        Импорт архива
      </b-button>
      <b-button variant="outline-primary" :to="{ name: 'strip-import-siq' }">
        <feather-icon icon="PackageIcon" class="mr-50" />
        Импорт SIQ
      </b-button>
    </div>

    <vue-good-table
      :columns="columns"
      :rows="stripsList"
      :pagination-options="{ enabled: true, perPage: filters.perPage }"
    >
      <template slot="table-row" slot-scope="props">
        <span v-if="props.column.field === 'questions_count'">
          <b-progress
            :value="props.row.strip_questions_count || 0"
            :max="5"
            :variant="progressVariant(props.row)"
            class="progress-bar-sm"
            style="min-width: 80px"
          />
          <small>{{ props.row.strip_questions_count || 0 }}/5</small>
        </span>

        <span v-else-if="props.column.field === 'difficulty'">
          <span v-for="n in 5" :key="n">
            <feather-icon
              icon="StarIcon"
              :class="n <= props.row.difficulty ? 'text-warning' : 'text-muted'"
            />
          </span>
        </span>

        <span v-else-if="props.column.field === 'created_at'">
          {{ formatDate(props.row.created_at) }}
        </span>

        <span v-else-if="props.column.field === 'action'">
          <b-link
            :to="{ name: 'strip-edit', params: { id: props.row.id } }"
            class="font-weight-bold mr-1"
          >
            <feather-icon icon="Edit2Icon" />
          </b-link>
          <b-link
            :to="{ name: 'strip-builder', params: { id: props.row.id } }"
            class="font-weight-bold mr-1"
            title="Открыть в построителе"
          >
            <feather-icon icon="ToolIcon" />
          </b-link>
          <feather-icon
            icon="TrashIcon"
            class="cursor-pointer text-danger"
            @click="confirmDelete(props.row)"
          />
        </span>

        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <template slot="pagination-bottom">
        <div class="d-flex justify-content-between flex-wrap">
          <b-pagination
            v-model="filters.page"
            :total-rows="total"
            :per-page="filters.perPage"
            first-number
            last-number
            class="mt-1 mb-0"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </div>
      </template>
    </vue-good-table>
  </b-card>
</template>

<script>
import {
  BCard, BButton, BPagination, BLink, BProgress,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import { onUnmounted, ref, watch } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { formatDate } from '@core/utils/filter'
import stripBuilderStoreModule from './builder/stripBuilderStoreModule'

export default {
  components: {
    BCard, BButton, BPagination, BLink, BProgress, VueGoodTable,
  },
  setup() {
    const STORE_MODULE_NAME = 'strip-builder'
    const toast = useToast()

    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, stripBuilderStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const columns = [
      { label: 'ID', field: 'id', type: 'number' },
      { label: 'Название', field: 'name' },
      { label: 'Автор', field: 'author_name' },
      { label: 'Вопросы', field: 'questions_count', sortable: false },
      { label: 'Сложность', field: 'difficulty', sortable: false },
      { label: 'Дата', field: 'created_at' },
      { label: 'Действия', field: 'action', sortable: false },
    ]

    const filters = ref({ page: 1, perPage: 25 })
    const stripsList = ref([])
    const total = ref(0)

    const progressVariant = row => {
      const count = row.strip_questions_count || 0
      if (count === 0) return 'danger'
      if (count === 5) return 'success'
      return 'warning'
    }

    const fetchList = () => {
      store.dispatch('strip-builder/fetchList', {
        page: filters.value.page,
        per_page: filters.value.perPage,
      })
        .then(response => {
          stripsList.value = response.data.data || []
          total.value = response.data.total || stripsList.value.length
        })
    }

    fetchList()

    watch(filters, () => {
      fetchList()
    }, { deep: true })

    const confirmDelete = row => {
      if (!window.confirm(`Удалить полоску «${row.name}»?`)) return
      store.dispatch('strip-builder/deleteStrip', { id: row.id })
        .then(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Полоска удалена',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          fetchList()
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка при удалении',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    return {
      columns, stripsList, filters, total, progressVariant, confirmDelete, formatDate,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
